import React from "react";

interface BlogPostsProps {
  blog_posts: { title: string; url: string; published_at: string, author: string, snippet: string, image_url: string }[];
}

export default function BlogPosts({ blog_posts }: BlogPostsProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 30) {
      return diffDays === 0 ? 'today' : `${diffDays}d ago`;
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <>
      <style>
        {`
          .blog-container {
            max-width: 800px;  /* Increased width to match Spotlight */
            margin: 0 auto;
            padding: 0 24px;
          }

          .blog-post {
            border-bottom: 1px solid rgba(230, 230, 230, 0.8);
            padding: 48px 0;
            transition: all 0.2s ease;
            cursor: pointer;
          }

          .blog-post:hover {
            background-color: rgba(250, 250, 250, 0.3);
            transform: translateY(-1px);
          }

          .blog-post:last-child {
            border-bottom: none;
          }

          .blog-title {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
            font-size: 28px;
            font-weight: 700;
            color: rgb(25, 25, 25);
            margin-bottom: 16px;
            line-height: 1.3;
            letter-spacing: -0.015em;
          }

          .blog-snippet {
            color: rgb(90, 90, 90);
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
            font-size: 18px;
            line-height: 1.7;
            margin-bottom: 20px;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            font-weight: 400;
          }

          .blog-meta {
            display: flex;
            align-items: center;
            gap: 12px;
            color: rgb(117, 117, 117);
            font-size: 15px;
            font-weight: 400;
          }

          .blog-image {
            width: 220px;
            height: 148px;
            object-fit: cover;
            border-radius: 12px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: transform 0.2s ease;
          }

          .blog-post:hover .blog-image {
            transform: scale(1.02);
          }

          .author-avatar {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          .author-name {
            color: rgb(25, 25, 25);
            font-size: 15px;
            font-weight: 500;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
          }

          .blog-post a {
            text-decoration: none;
            color: inherit;
          }

          .blog-post a:hover {
            text-decoration: none;
          }

          @media (max-width: 768px) {
            .blog-container {
              padding: 0 16px;
            }

            .blog-post {
              padding: 32px 0;
            }

            .blog-image {
              width: 120px;
              height: 120px;
            }

            .blog-title {
              font-size: 22px;
              line-height: 1.35;
            }

            .blog-snippet {
              font-size: 16px;
              line-height: 1.6;
            }

            .author-avatar {
              width: 24px;
              height: 24px;
            }

            .author-name {
              font-size: 14px;
            }

            .blog-meta {
              font-size: 14px;
            }
          }

          .dark-mode .blog-post {
            border-bottom-color: #322f2f;
          }

          .dark-mode .blog-post:hover {
            background-color: rgba(255, 255, 255, 0.03);
          }

          .dark-mode .blog-title,
          .dark-mode .author-name {
            color: #ffffff;
          }

          .dark-mode .blog-snippet,
          .dark-mode .blog-meta {
            color: #9c9fa6;
          }
        `}
      </style>

      <div className="blog-container">
        {blog_posts.map((blog_post) => (
          <article key={blog_post.url} className="blog-post flex gap-6 items-start">
            <div className="flex-grow">
              <a href={blog_post.url} className="block">
                <div className="flex items-center gap-2 mb-3">
                  <img
                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(blog_post.author)}&background=random`}
                    alt={blog_post.author}
                    className="author-avatar"
                  />
                  <span className="author-name">{blog_post.author}</span>
                </div>

                <h2 className="blog-title">
                  {blog_post.title}
                </h2>
                <p className="blog-snippet">
                  {blog_post.snippet}
                </p>
              </a>
            </div>

            <div className="flex-shrink-0 flex flex-col">
              <a href={blog_post.url} className="block">
                {blog_post.image_url && (
                  <img
                    src={blog_post.image_url}
                    alt=""
                    className="blog-image mb-3"
                  />
                )}
              </a>
              <div className="blog-meta">
                <time>{formatDate(blog_post.published_at)}</time>
              </div>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
